/**
 * Motion path sheet (W2-11 batch-tools; DESIGN.md §4.14, §5.8; RPE Complex move CMOVE-01..06, E§4.12).
 * Presets (line, arc, circle, spiral, figure-8, sine) or expressions x(t), y(t), r(t) with live errors; the
 * preset's equivalent expressions are shown and can be turned into editable expressions; time curve; linear keys
 * per beat with the max error in px, or an adaptive fit within a tolerance; new layer by default (beat-0 Stay key)
 * or a chosen layer with replace / refuse. The path is drawn green -> red in the preview while the sheet is open.
 * "Start from the line's position" (default on) moves the path so it starts where the line already is
 * (`anchoredPreset`): no jump at the start beat, and closed shapes end where they started. The expression box
 * always shows what is applied, x(t), y(t) and r(t), after that move. The hints follow the choices: anchored, a
 * centre field is disabled ("makes no difference") and line / sine say only the step from From to To counts; not
 * anchored, a new layer adds the path to the line, an existing layer takes the path's values over the span.
 */

import { useEffect, useMemo, useState } from 'react';
import type { DialogProps } from '../../../app/registry/types';
import { LINEAR } from '../../../core/ease/index';
import type { Ease } from '../../../core/ease/types';
import type { LineId } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import type { Beat } from '../../../core/time/types';
import { Tools } from '../../../core/tools/index';
import { anchoredPreset } from '../../../core/tools/motionPath';
import {
  anchorEffect,
  compilePath,
  pathExpressions,
  placementFields,
  PATH_PRESET_DEFAULTS,
  PATH_PRESET_KINDS,
  type PathPresetKind,
} from '../../../core/tools/pathPresets';
import { freeCommonLayer } from '../../../core/tools/write';
import type { MotionPathParams, PathPreset, ToolReport } from '../../../core/tools/types';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { EmptyState } from '../../../ui/EmptyState';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { TextField } from '../../../ui/TextField';
import type { Json } from '../../../core/util/types';
import { m } from '../batch.messages';
import s from '../Batch.module.css';
import {
  DEFAULT_PATH_PER_BEAT,
  EXT,
  extNumber,
  extObject,
  isBool,
  isNum,
  pick,
  playheadBeat,
  ref,
  remember,
  tempoOf,
  toastNotes,
  workingLine,
} from '../batchContext';
import { setPathPreview } from '../pathOverlay';
import {
  BeatInput,
  CurveField,
  ExpressionBox,
  NumberInput,
  ToolSheet,
  useBeatContext,
  useText,
  type SheetStatus,
  type Text,
} from '../sheetKit';
import { useToolGesture } from '../useToolGesture';

const PRESET_LABEL: Readonly<Record<PathPresetKind, string>> = {
  line: m.presetLine,
  arc: m.presetArc,
  circle: m.presetCircle,
  spiral: m.presetSpiral,
  figure8: m.presetFigure8,
  sine: m.presetSine,
  expr: m.presetExpr,
};

type Presets = { readonly [K in PathPresetKind]: Extract<PathPreset, { kind: K }> };

/** A number of a preset: a scalar field, or one coordinate of a point field. */
interface PresetField {
  readonly label: string;
  readonly key: string;
  readonly index?: 0 | 1;
}

const POINT = (key: string, x: string, y: string): PresetField[] => [
  { label: x, key, index: 0 },
  { label: y, key, index: 1 },
];

export const PRESET_FIELDS: Readonly<Record<Exclude<PathPresetKind, 'expr'>, readonly PresetField[]>> = {
  line: [...POINT('from', m.fromX, m.fromY), ...POINT('to', m.toX, m.toY)],
  arc: [
    ...POINT('center', m.centerX, m.centerY),
    { label: m.radius, key: 'radius' },
    { label: m.fromDeg, key: 'fromDeg' },
    { label: m.toDeg, key: 'toDeg' },
  ],
  circle: [
    ...POINT('center', m.centerX, m.centerY),
    { label: m.radius, key: 'radius' },
    { label: m.turns, key: 'turns' },
    { label: m.startDeg, key: 'startDeg' },
  ],
  spiral: [
    ...POINT('center', m.centerX, m.centerY),
    { label: m.radiusStart, key: 'r0' },
    { label: m.radiusEnd, key: 'r1' },
    { label: m.turns, key: 'turns' },
  ],
  figure8: [...POINT('center', m.centerX, m.centerY), { label: m.width, key: 'w' }, { label: m.height, key: 'h' }],
  sine: [
    ...POINT('from', m.fromX, m.fromY),
    ...POINT('to', m.toX, m.toY),
    { label: m.amplitude, key: 'amp' },
    { label: m.waves, key: 'waves' },
  ],
};

export function readPresetField(p: PathPreset, f: PresetField): number {
  const v = (p as unknown as Readonly<Record<string, unknown>>)[f.key];
  if (f.index === undefined) return typeof v === 'number' ? v : 0;
  return Array.isArray(v) ? Number(v[f.index]) : 0;
}

export function writePresetField<P extends PathPreset>(p: P, f: PresetField, v: number): P {
  const rec = p as unknown as Readonly<Record<string, unknown>>;
  const cur = rec[f.key];
  let next: unknown = v;
  if (f.index !== undefined) {
    const pt = Array.isArray(cur) ? (cur as readonly number[]) : [0, 0];
    next = f.index === 0 ? [v, pt[1] ?? 0] : [pt[0] ?? 0, v];
  }
  return { ...p, [f.key]: next };
}

function axisLabel(text: Text, axis: 'x' | 'y' | 'r'): string {
  return text.t(axis === 'x' ? m.exprX : axis === 'y' ? m.exprY : m.exprR);
}

export function MotionPathSheet(p: DialogProps<'motionPath'>) {
  const { services, props } = p;
  const text = useText();
  const store = services.store;
  const beatCtx = useBeatContext(services);
  const saved = useMemo(() => extObject(services, EXT.path), [services]);
  const line = useMemo<LineId | null>(() => {
    if (props.line !== null && store?.doc.lines.has(props.line) === true) return props.line;
    return workingLine(services);
  }, [props.line, store, services]);

  // Span: the selected segments' span on the line, else 4 beats from the playhead.
  const [initialSpan] = useState<readonly [Beat, Beat]>(() => {
    if (store !== null && props.segments.length > 0) {
      let lo: Beat | null = null;
      let hi: Beat | null = null;
      for (const id of props.segments) {
        const k = store.doc.keys.get(id);
        const next = store.idx.nextKey(id);
        const n = next === undefined ? undefined : store.doc.keys.get(next);
        if (k === undefined || n === undefined) continue;
        if (lo === null || Beats.lt(k.t, lo)) lo = k.t;
        if (hi === null || Beats.lt(hi, n.t)) hi = n.t;
      }
      if (lo !== null && hi !== null) return [lo, hi];
    }
    const at = playheadBeat(services);
    return [at, Beats.add(at, Beats.fromInt(4))];
  });
  const [t0, setT0] = useState<Beat>(initialSpan[0]);
  const [t1, setT1] = useState<Beat>(initialSpan[1]);
  const [kind, setKind] = useState<PathPresetKind>('circle');
  const [presets, setPresets] = useState<Presets>(PATH_PRESET_DEFAULTS);
  const [draft, setDraft] = useState<{ x: string; y: string; r: string }>(() => {
    const e = PATH_PRESET_DEFAULTS.expr;
    return { x: e.x, y: e.y, r: e.r ?? '' };
  });
  const [timeEase, setTimeEase] = useState<Ease>(LINEAR);
  const [outputMode, setOutputMode] = useState<'linear' | 'fit'>('linear');
  const [perBeat, setPerBeat] = useState(() => extNumber(services, EXT.pathPerBeat, DEFAULT_PATH_PER_BEAT, 1, 1920));
  const [tolPx, setTolPx] = useState(() => pick(saved, 'tolPx', isNum, 0.5));
  const [layer, setLayer] = useState<'new' | number>('new');
  const [conflict, setConflict] = useState<'replace' | 'refuse'>('replace');
  const [fromLine, setFromLine] = useState(() => pick(saved, 'fromLine', isBool, true));

  const preset: PathPreset = presets[kind];
  const compiled = useMemo(() => compilePath(preset), [preset]);
  const draftErrors = useMemo(() => {
    const c = compilePath({ kind: 'expr', x: draft.x, y: draft.y, r: draft.r });
    return c.ok ? null : c.error;
  }, [draft]);

  const params = useMemo<MotionPathParams | null>(() => {
    if (line === null || !compiled.ok || !Beats.lt(t0, t1)) return null;
    return {
      line,
      layer,
      t0,
      t1,
      preset,
      timeEase,
      output: outputMode === 'linear' ? { mode: 'linear', perBeat } : { mode: 'fit', tolPx },
      conflict,
    };
  }, [line, compiled, t0, t1, layer, preset, timeEase, outputMode, perBeat, tolPx, conflict]);

  // The overlay span is published before the gesture's first preview (effects run in order).
  const tempo = tempoOf(services);
  useEffect(() => {
    if (line === null || tempo === null || !Beats.lt(t0, t1)) {
      setPathPreview(null);
      return undefined;
    }
    setPathPreview({ line, t0: tempo.secAt(t0), t1: tempo.secAt(t1) });
    return () => {
      setPathPreview(null);
    };
  }, [line, tempo, t0, t1]);

  // The move to the line's position is measured on the transaction (the document without the preview).
  const build = useMemo(
    () =>
      params === null
        ? null
        : (tx: Parameters<typeof Tools.motionPath>[0]) =>
            Tools.motionPath(tx, fromLine ? { ...params, preset: anchoredPreset(tx, params) } : params),
    [params, fromLine],
  );
  const gesture = useToolGesture<ToolReport>({ services, label: ref(m.txnPath), command: 'tools.path', build });

  // Layers of the line for the layer choice (read before the preview adds one).
  const [layerInfo] = useState(() => {
    if (store === null || line === null) return { used: [] as number[], free: null as number | null };
    const used: number[] = [];
    for (let l = 0; l < 4; l++) {
      if (store.idx.track(line, 'moveX', l) !== undefined || store.idx.track(line, 'moveY', l) !== undefined)
        used.push(l);
    }
    return { used, free: freeCommonLayer(store, line, ['moveX', 'moveY']) };
  });

  const cancel = (): void => {
    gesture.cancel();
    p.close(null);
  };
  if (line === null) {
    return (
      <ToolSheet
        title={text.t(m.pathTitle)}
        status={{ kind: 'none' }}
        applyLabel={text.t(m.apply)}
        applyDisabledReason={ref(m.pathNeedsLine)}
        onApply={() => undefined}
        onCancel={cancel}
      >
        <EmptyState icon="Route" title={ref(m.pathNeedsLine)} body={ref(m.pathNeedsLineBody)} />
      </ToolSheet>
    );
  }

  const outcome = gesture.outcome;
  let status: SheetStatus = { kind: 'none' };
  if (!Beats.lt(t0, t1)) status = { kind: 'error', text: text.t(m.pathRangeInvalid) };
  else if (!compiled.ok)
    status = { kind: 'error', text: text.t(m.pathExprInvalid, { axis: axisLabel(text, compiled.error.axis) }) };
  else if (gesture.status === 'paused') status = { kind: 'warn', text: text.t(m.previewPaused) };
  else if (outcome !== null && !outcome.ok) status = { kind: 'error', text: text.tr(outcome.reason) };
  else if (outcome?.ok === true) {
    status = {
      kind: 'ok',
      text: text.t(m.pathStatus, {
        keys: Math.round(outcome.value.changed / (compiled.value.rotates ? 3 : 2)),
        error: Number((outcome.value.maxError ?? 0).toPrecision(2)),
      }),
    };
  }

  const apply = (): void => {
    const out = gesture.apply();
    if (out === null || !out.ok) return;
    remember(services, EXT.pathPerBeat, perBeat);
    const value: Json = { tolPx, fromLine };
    remember(services, EXT.path, value);
    const perAxis = compiled.ok ? Math.round(out.value.changed / (compiled.value.rotates ? 3 : 2)) : out.value.changed;
    services.ui.toast(ref(m.pathDone, { keys: perAxis }), { kind: 'success', undo: out.txn !== null });
    toastNotes(services, out.notes);
    p.close({ applied: true });
  };

  // What is applied (shown in the expression box): the preset, moved to the line's position when asked.
  const applied =
    fromLine && params !== null && store !== null && tempo !== null
      ? anchoredPreset({ doc: store.doc, idx: store.idx, tempo }, params)
      : preset;
  const exprs = pathExpressions(applied);
  const typed = pathExpressions(preset);
  const showApplied = kind !== 'expr' || exprs.x !== typed.x || exprs.y !== typed.y || exprs.r !== typed.r;
  const exprLines = [`x(t) = ${exprs.x}`, `y(t) = ${exprs.y}`];
  if (exprs.r !== null) exprLines.push(`r(t) = ${exprs.r}`);
  const exprText = exprLines.join('\n');
  const placementOnly = fromLine ? placementFields(kind) : [];
  const effect = fromLine ? anchorEffect(kind) : 'none';
  // What the toggle means depends on the layer: a new layer adds the path to the line, an existing layer takes the
  // path's values over the span.
  const placementHint = fromLine
    ? text.t(m.pathFromLineHint)
    : layer === 'new'
      ? text.t(m.pathAbsoluteHint)
      : text.t(m.pathAbsoluteLayerHint, { layer });
  const editAsExpressions = (): void => {
    const next = { kind: 'expr' as const, x: exprs.x, y: exprs.y, ...(exprs.r === null ? {} : { r: exprs.r }) };
    setDraft({ x: next.x, y: next.y, r: exprs.r ?? '' });
    setPresets((ps) => ({ ...ps, expr: next }));
    setKind('expr');
  };
  const commitExpr = (axis: 'x' | 'y' | 'r', v: string): void => {
    setPresets((ps) => {
      const e = ps.expr;
      const next =
        axis === 'r'
          ? v.trim() === ''
            ? { kind: 'expr' as const, x: e.x, y: e.y }
            : { ...e, r: v }
          : { ...e, [axis]: v };
      return { ...ps, expr: next };
    });
  };

  const layerOptions: { value: string; label: string }[] = [
    {
      value: 'new',
      label: layerInfo.free === null ? text.t(m.layerNewNone) : text.t(m.layerNew, { layer: layerInfo.free }),
    },
    ...[0, 1, 2, 3].map((l) => ({
      value: String(l),
      label: text.t(layerInfo.used.includes(l) ? m.layerUsed : m.layerFree, { layer: l }),
    })),
  ];

  return (
    <ToolSheet
      title={text.t(m.pathTitle)}
      subtitle={text.t(m.pathLine, { line: store?.doc.lines.get(line)?.name ?? '' })}
      status={status}
      applyLabel={text.t(m.apply)}
      applyDisabledReason={params === null ? ref(m.pathFixFields) : null}
      onApply={apply}
      onCancel={cancel}
    >
      <Select<PathPresetKind>
        label={text.t(m.preset)}
        value={kind}
        options={PATH_PRESET_KINDS.map((k) => ({ value: k, label: text.t(PRESET_LABEL[k]) }))}
        onChange={setKind}
      />
      <div className={s.row}>
        <BeatInput label={text.t(m.pathStart)} value={t0} ctx={beatCtx} onChange={setT0} />
        <BeatInput label={text.t(m.pathEnd)} value={t1} ctx={beatCtx} onChange={setT1} />
      </div>
      {kind === 'expr' ? (
        <div className={s.group}>
          {(['x', 'y', 'r'] as const).map((axis) => (
            <div key={axis}>
              <TextField
                label={axisLabel(text, axis)}
                // The committed expression: the field keeps its own text while typing (the draft only drives the
                // live error), and Enter or leaving the field commits it to the path.
                value={axis === 'r' ? (presets.expr.r ?? '') : presets.expr[axis]}
                onChange={(v) => {
                  setDraft((d) => ({ ...d, [axis]: v }));
                }}
                onCommit={(v) => {
                  setDraft((d) => ({ ...d, [axis]: v }));
                  commitExpr(axis, v);
                }}
                {...(axis === 'r' ? { placeholder: ref(m.exprROptional) } : {})}
              />
              {draftErrors?.axis === axis && (
                <p className={s.error} role="alert">
                  {text.tr(draftErrors.error)}
                </p>
              )}
            </div>
          ))}
          <p className={s.hint}>{text.t(m.exprHelp)}</p>
        </div>
      ) : (
        <div className={s.group}>
          <div className={s.fields}>
            {PRESET_FIELDS[kind].map((f) => (
              <NumberInput
                key={`${f.key}:${String(f.index ?? '')}`}
                label={text.t(f.label)}
                value={readPresetField(preset, f)}
                disabled={placementOnly.includes(f.key)}
                onChange={(v) => {
                  setPresets((ps) => ({ ...ps, [kind]: writePresetField(ps[kind], f, v) }));
                }}
              />
            ))}
          </div>
          {effect === 'centre' && placementOnly.length > 0 && <p className={s.hint}>{text.t(m.pathCentreIgnored)}</p>}
          {effect === 'ends' && <p className={s.hint}>{text.t(m.pathEndsRelative)}</p>}
        </div>
      )}
      <Checkbox className={s.check} label={text.t(m.pathFromLine)} checked={fromLine} onChange={setFromLine} />
      <p className={s.hint}>{placementHint}</p>
      {showApplied && (
        <ExpressionBox
          services={services}
          label={text.t(kind === 'expr' ? m.appliedExpressions : m.equivalentExpression)}
          code={exprText}
        >
          {kind !== 'expr' && (
            <Button variant="ghost" size="sm" onClick={editAsExpressions}>
              {text.t(m.editAsExpressions)}
            </Button>
          )}
        </ExpressionBox>
      )}
      <CurveField services={services} label={text.t(m.timeCurve)} value={timeEase} onChange={setTimeEase} />
      <Segmented<'linear' | 'fit'>
        label={text.t(m.output)}
        value={outputMode}
        options={[
          { value: 'linear', label: text.t(m.outputLinear) },
          { value: 'fit', label: text.t(m.outputFit) },
        ]}
        onChange={setOutputMode}
      />
      {outputMode === 'linear' ? (
        <NumberInput label={text.t(m.keysPerBeat)} value={perBeat} integer min={1} max={1920} onChange={setPerBeat} />
      ) : (
        <NumberInput
          label={text.t(m.tolerancePx)}
          value={tolPx}
          min={0.01}
          max={100}
          step={0.1}
          unit={text.t(m.unitPx)}
          onChange={setTolPx}
        />
      )}
      <div className={s.row}>
        <Select<string>
          label={text.t(m.layer)}
          value={layer === 'new' ? 'new' : String(layer)}
          options={layerOptions}
          onChange={(v) => {
            setLayer(v === 'new' ? 'new' : Number(v));
          }}
        />
        {layer !== 'new' && (
          <Segmented<'replace' | 'refuse'>
            label={text.t(m.pathOverlap)}
            value={conflict}
            options={[
              { value: 'replace', label: text.t(m.overlapReplace) },
              { value: 'refuse', label: text.t(m.overlapRefuse) },
            ]}
            onChange={setConflict}
          />
        )}
      </div>
      <p className={s.hint}>{text.t(m.pathHint)}</p>
    </ToolSheet>
  );
}
